import React, { useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import { NotificationItem } from '@/features/notifications/components/NotificationItem';

export const NotificationDropdown: React.FC = () => {
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);

    const handleNotificationClick = async (id: string, link?: string) => {
        await markAsRead(id);
        setIsOpen(false);
        if (link) {
            // Check if it's a full URL
            if (link.startsWith('http://') || link.startsWith('https://')) {
                try {
                    const url = new URL(link);
                    // Check if it's our domain - extract path and navigate internally
                    if (url.hostname === window.location.hostname) {
                        navigate(url.pathname + url.search + url.hash);
                    } else {
                        // External URL - open in new tab
                        window.open(link, '_blank', 'noopener,noreferrer');
                    }
                } catch {
                    navigate(link);
                }
            } else {
                navigate(link);
            }
        }
    };

    const handleViewAll = () => {
        setIsOpen(false);
        navigate('/notifications');
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full bg-gray-50 hover:bg-gray-100 text-gray-600 relative" aria-label="Notifications">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white shadow-sm border-2 border-white">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 sm:w-96 shadow-xl border-border/60">
                <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
                    <DropdownMenuLabel className="font-bold text-base p-0">Notifications</DropdownMenuLabel>
                    {unreadCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto text-xs text-primary px-2 hover:bg-primary/10"
                            onClick={() => markAllAsRead()}
                        >
                            <CheckCheck className="h-3 w-3 mr-1" />
                            Mark all read
                        </Button>
                    )}
                </div>
                <DropdownMenuSeparator className="m-0" />
                <ScrollArea className="h-[350px]">
                    {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground p-4 text-center">
                            <div className="bg-muted p-4 rounded-full mb-3">
                                <Bell className="h-6 w-6 opacity-30" />
                            </div>
                            <h4 className="font-semibold text-sm mb-1">No notifications</h4>
                            <p className="text-xs">We'll let you know when something happens.</p>
                        </div>
                    ) : (
                        <div className="py-1">
                            {notifications.slice(0, 10).map((notification) => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    onClick={handleNotificationClick}
                                />
                            ))}
                        </div>
                    )}
                </ScrollArea>
                <DropdownMenuSeparator className="m-0" />
                <div className="p-2 text-center bg-muted/30">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs text-primary"
                        onClick={handleViewAll}
                    >
                        View all notifications
                    </Button>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

